import React, { useEffect } from "react";
import { Box, Button, Stack, SvgIcon, Typography } from "@mui/material";
import Logo from "src/assets/svg-icons/arsann-logo.svg";
import { ReactComponent as AppleSvg } from "src/assets/svg-icons/ios.svg";
import { ReactComponent as AndroidSvg } from "src/assets/svg-icons/android.svg";
import { useTranslation } from "react-i18next";

const APP_STORE_URL =
  "https://apps.apple.com/sa/app/arsann-integrated-solutions/id6757977584";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.arsann.customer.app&pcampaignid=web_share";

const getMobileStoreUrl = () => {
  const userAgent = navigator.userAgent || navigator.vendor || "";

  // iPhone / iPad / iPod
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return APP_STORE_URL;
  }

  // iPadOS can sometimes report itself as Mac
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) {
    return APP_STORE_URL;
  }

  // Android
  if (/Android/i.test(userAgent)) {
    return GOOGLE_PLAY_URL;
  }

  return null;
};

const DownloadCustomerApp: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const storeUrl = getMobileStoreUrl();

    if (storeUrl) {
      window.location.replace(storeUrl);
    }
  }, []);

  const handleAppStoreClick = () => {
    window.location.href = APP_STORE_URL;
  };

  const handleGooglePlayClick = () => {
    window.location.href = GOOGLE_PLAY_URL;
  };

  const mobileStoreUrl = getMobileStoreUrl();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#000",
        color: "#fff",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Stack
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: "500px",
          minHeight: "100vh",
          px: 2.5,
          py: 4,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            mt: 8,
            mb: 4,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={Logo}
            alt="Arsann"
            sx={{
              width: 100,
              height: 100,
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontSize: {
              xs: "28px",
              sm: "30px",
            },
            fontWeight: 700,
            textAlign: "center",
            mb: 1,
          }}
        >
          حمّل التطبيق
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: "16px",
            color: "#999",
            textAlign: "center",
            mb: 8,
            direction: i18n.language === "ar" ? "ltr" : "rtl",
          }}
        >
          حلول المواقف المتكاملة · Integrated Parking Solutions
        </Typography>

        {/* App Store */}
        {(!mobileStoreUrl || mobileStoreUrl === APP_STORE_URL) && (
          <Button
            fullWidth
            onClick={handleAppStoreClick}
            sx={{
              minHeight: "80px",
              mb: 2,
              px: 3,
              border: "1px solid #4b3b13",
              borderRadius: "16px",
              bgcolor: "#111",
              color: "#fff",
              textTransform: "none",
              fontSize: "18px",
              fontWeight: 500,
              transition: "all 0.2s ease",

              "&:hover": {
                bgcolor: "#181818",
                borderColor: "#80651e",
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
            >
              <Typography component="span" sx={{ color: "#aaa" }}>
                آبل ستور
              </Typography>

              <Typography component="span">· App Store</Typography>

              <SvgIcon
                component={AppleSvg}
                color="primary"
                inheritViewBox
                sx={{
                  // color: "inherit",
                  fontSize: 24,
                }}
              />
            </Stack>
          </Button>
        )}

        {/* Google Play */}
        {(!mobileStoreUrl || mobileStoreUrl === GOOGLE_PLAY_URL) && (
          <Button
            fullWidth
            onClick={handleGooglePlayClick}
            sx={{
              minHeight: "80px",
              px: 3,
              border: "1px solid #4b3b13",
              borderRadius: "16px",
              bgcolor: "#111",
              color: "#fff",
              textTransform: "none",
              fontSize: "18px",
              fontWeight: 500,
              transition: "all 0.2s ease",

              "&:hover": {
                bgcolor: "#181818",
                borderColor: "#80651e",
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
            >
              <Typography component="span" sx={{ color: "#aaa" }}>
                جوجل بلاي
              </Typography>

              <Typography component="span">· Google Play</Typography>

              <SvgIcon
                component={AndroidSvg}
                inheritViewBox
                color="primary"
                sx={{
                  fontSize: 24,
                }}
              />
            </Stack>
          </Button>
        )}

        {/* Footer */}
        <Typography
          sx={{
            mt: 5,
            color: "#444",
            fontSize: "14px",
          }}
        >
          Arsann ©
        </Typography>
      </Stack>
    </Box>
  );
};

export default DownloadCustomerApp;
